import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AthleteContext } from "./App";
import FilterTags from "./FilterTags";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Onboarding() {
    const { currentAthlete, setCurrentAthlete, sessionChecked } = useContext(AthleteContext);
    const navigate = useNavigate();

    const [csrfToken, setCsrfToken] = useState(null);
    const [nickname, setNickname] = useState("");
    const [eventFilters, setEventFilters] = useState([]);
    const [trainingFilters, setTrainingFilters] = useState([]);
    const [bio, setBio] = useState("");
    const [targetAmount, setTargetAmount] = useState(0);
    const [participationType, setParticipationType] = useState("none");
    const [participationDays, setParticipationDays] = useState([]);
    const [overnightDays, setOvernightDays] = useState([]);
    const [availableDays, setAvailableDays] = useState([]);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        fetch(`${API}/api/csrf-token`, { credentials: "include" })
            .then(r => r.json())
            .then(data => setCsrfToken(data.csrfToken));
        fetch(`${API}/event-routes`)
            .then(r => r.json())
            .then(data => setAvailableDays(Array.isArray(data) ? data : []))
            .catch(() => {});
    }, []);

    // Pre-fill form whenever the athlete from context becomes available
    useEffect(() => {
        if (!currentAthlete) return;
        setNickname(currentAthlete.nickname || "");
        setEventFilters(currentAthlete.eventFilters || []);
        setTrainingFilters(currentAthlete.trainingFilters || []);
        setBio(currentAthlete.bio || "");
        setParticipationType(currentAthlete.participationType || "none");
        setParticipationDays(currentAthlete.participationDays || []);
        setOvernightDays(currentAthlete.overnightDays || []);
        setTargetAmount(currentAthlete.targetAmount || 0);
    }, [currentAthlete]);

    const allOvernightDays = (days) => days.slice(0, -1).map(d => d.dayNumber);

    const handleTypeChange = (type) => {
        setParticipationType(type);
        if (type === "full" && availableDays.length > 0) {
            setOvernightDays(allOvernightDays(availableDays));
        }
    };

    const toggleDay = (dayNumber) => {
        setParticipationDays(prev =>
            prev.includes(dayNumber) ? prev.filter(d => d !== dayNumber) : [...prev, dayNumber].sort((a, b) => a - b)
        );
    };

    const toggleOvernight = (dayNumber) => {
        setOvernightDays(prev =>
            prev.includes(dayNumber) ? prev.filter(d => d !== dayNumber) : [...prev, dayNumber].sort((a, b) => a - b)
        );
    };

    // Periodiek sessie controleren — redirect naar aanmelden als verlopen
    useEffect(() => {
        const check = () => {
            fetch(`${API}/athlete/session`, { credentials: "include" })
                .then(r => r.json())
                .then(data => { if (!data.authenticated) navigate("/aanmelden"); });
        };
        const interval = setInterval(check, 4 * 60 * 1000);
        return () => clearInterval(interval);
    }, [navigate]);

    const save = () => {
        fetch(`${API}/athlete/onboarding`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json", "CSRF-Token": csrfToken },
            body: JSON.stringify({ nickname, bio, eventFilters, trainingFilters, participationType, participationDays,
                overnightDays: participationType === "partial"
                    ? overnightDays.filter(d => participationDays.includes(d) && participationDays.includes(d + 1))
                    : overnightDays,
                targetAmount })
        })
            .then(r => {
                if (r.status === 401) { navigate("/aanmelden"); return null; }
                return r.json();
            })
            .then(data => {
                if (!data) return;
                setCurrentAthlete(a => ({ ...a, nickname, bio, eventFilters, trainingFilters, participationType, participationDays, overnightDays, targetAmount }));
                setSaved(true);
            });
    };

    const logout = () => {
        fetch(`${API}/athlete/logout`, {
            method: "POST",
            credentials: "include",
            headers: { "CSRF-Token": csrfToken }
        })
            .then(() => {
                setCurrentAthlete(null);
                navigate("/");
            });
    };

    // Wait until the app-level session check has finished before deciding what to show
    if (!sessionChecked) return <p>Laden…</p>;

    if (!currentAthlete) {
        return (
            <div className="pane pane-center dark_pane">
                <h1>Niet ingelogd</h1>
                <p>Je bent niet aangemeld. <a href="/aanmelden">Meld je aan via Strava</a> om je profiel in te stellen.</p>
            </div>
        );
    }

    return (
        <div className="pane pane-center dark_pane">
            <div className="profile-header">
                {currentAthlete.profilePhoto && (
                    <img src={currentAthlete.profilePhoto} alt={currentAthlete.firstName} className="profile-photo" />
                )}
                <h1>Mijn profiel</h1>
            </div>
            <p>Welkom, <strong>{currentAthlete.firstName} {currentAthlete.lastName}</strong>.</p>

            {!currentAthlete.approved && (
                <p className="pending-notice">
                    Je aanmelding wacht nog op goedkeuring door de beheerder. Je routes worden daarna gesynchroniseerd.
                </p>
            )}

            <label htmlFor="nickname">Weergavenaam (optioneel)</label>
            <input
                id="nickname"
                value={nickname}
                onChange={e => setNickname(e.target.value)}
                placeholder="Naam of nickname"
            />

            <label htmlFor="bio">Persoonlijke tekst (optioneel)</label>
            <small>Korte tekst over jezelf — zichtbaar op jouw profielpagina. Alleen vetgedrukt en cursief zijn toegestaan.</small>
            <textarea
                id="bio"
                value={bio}
                onChange={e => setBio(e.target.value)}
                placeholder="Vertel iets over jezelf…"
                rows={4}
                style={{ width: "100%", boxSizing: "border-box", marginBottom: "0.5rem" }}
            />

            <label>Activiteitsfilter evenementen</label>
            <small>Zoektermen die in de naam of beschrijving van een Strava-activiteit voorkomen.</small>
            <FilterTags
                filters={eventFilters}
                onChange={setEventFilters}
                placeholder="bijv. Hartekind"
            />

            <label>Activiteitsfilter trainingen</label>
            <small>Zelfde als boven, maar gemarkeerd als training op de kaart.</small>
            <FilterTags
                filters={trainingFilters}
                onChange={setTrainingFilters}
                placeholder="bijv. training"
            />

            <label htmlFor="targetAmount">Streefbedrag (€)</label>
            <small>Hoeveel donaties wil je ophalen? Wordt getoond op jouw profielpagina.</small>
            <div className="ap-amount-row">
                <span className="ap-eur">€</span>
                <input
                    id="targetAmount"
                    type="number"
                    min="0"
                    step="50"
                    value={targetAmount || ""}
                    onChange={e => setTargetAmount(Number(e.target.value) || 0)}
                    placeholder="0"
                    className="ap-amount-input"
                />
            </div>

            <label>Hoe wil je meedoen?</label>
            <div className="onboarding-participation">

                {/* ── Hele actie ── */}
                <div
                    className={`ob-option${participationType === "full" ? " selected" : ""}`}
                    onClick={() => handleTypeChange("full")}
                >
                    <div className="ob-option-header">
                        <span className="ob-circle">{participationType === "full" ? "✓" : ""}</span>
                        <span>Ik doe mee aan de hele grote actie</span>
                    </div>
                    {participationType === "full" && availableDays.length > 0 && (
                        <div className="ob-sub" onClick={e => e.stopPropagation()}>
                            <span className="ob-sub-label">Na welke etappes wil je overnachten?</span>
                            {availableDays.slice(0, -1).map(day => {
                                const on = overnightDays.includes(day.dayNumber);
                                return (
                                    <div key={day.dayNumber}
                                        className={`ob-overnight-full${on ? " selected" : ""}`}
                                        onClick={() => toggleOvernight(day.dayNumber)}
                                    >
                                        <span className={`ob-square${on ? " selected" : ""}`}>{on ? "✓" : ""}</span>
                                        <span>Na dag {day.dayNumber} — {day.name}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* ── Deel van de actie ── */}
                <div
                    className={`ob-option${participationType === "partial" ? " selected" : ""}`}
                    onClick={() => handleTypeChange("partial")}
                >
                    <div className="ob-option-header">
                        <span className="ob-circle">{participationType === "partial" ? "✓" : ""}</span>
                        <span>Ik doe mee aan een deel van de grote actie</span>
                    </div>
                    {participationType === "partial" && availableDays.length > 0 && (
                        <div className="ob-sub" onClick={e => e.stopPropagation()}>
                            {availableDays.map((day, idx) => {
                                const checked = participationDays.includes(day.dayNumber);
                                const isLast = idx === availableDays.length - 1;
                                const nextDay = availableDays[idx + 1];
                                const canOvernight = checked && !isLast && nextDay && participationDays.includes(nextDay.dayNumber);
                                const isOvernighting = overnightDays.includes(day.dayNumber);
                                return (
                                    <div key={day.dayNumber} className={`ob-day${checked ? " selected" : ""}`}
                                        onClick={() => toggleDay(day.dayNumber)}>
                                        <div className="ob-day-header">
                                            <span className={`ob-square${checked ? " selected" : ""}`}>{checked ? "✓" : ""}</span>
                                            <span>
                                                <strong>Dag {day.dayNumber}</strong> — {day.name}
                                                {day.distanceKm > 0 && <small> ({day.distanceKm} km)</small>}
                                            </span>
                                        </div>
                                        {checked && !isLast && (
                                            <div
                                                className={`ob-overnight${canOvernight && isOvernighting ? " selected" : ""}${!canOvernight ? " disabled" : ""}`}
                                                onClick={e => { e.stopPropagation(); if (canOvernight) toggleOvernight(day.dayNumber); }}
                                            >
                                                <span className={`ob-square${canOvernight && isOvernighting ? " selected" : ""}${!canOvernight ? " disabled" : ""}`}>
                                                    {canOvernight && isOvernighting ? "✓" : ""}
                                                </span>
                                                <span>Overnachten na dag {day.dayNumber}</span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* ── Individueel / nog niet besloten ── */}
                {[
                    { value: "individual", label: "Ik doe zelf individuele acties (lopen, fietsen, etc.)" },
                    { value: "none",       label: "Nog niet besloten" }
                ].map(opt => (
                    <div key={opt.value}
                        className={`ob-option${participationType === opt.value ? " selected" : ""}`}
                        onClick={() => handleTypeChange(opt.value)}
                    >
                        <div className="ob-option-header">
                            <span className="ob-circle">{participationType === opt.value ? "✓" : ""}</span>
                            <span>{opt.label}</span>
                        </div>
                    </div>
                ))}

            </div>

            <div className="onboarding-actions">
                <button onClick={save}>Opslaan</button>
                <button className="btn-ghost" onClick={logout}>Afmelden</button>
                {saved && <span className="save-success-inline">✓ Opgeslagen</span>}
            </div>
        </div>
    );
}
